import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Building2, Users, BarChart3, Mail, UserPlus, Trash2, Pencil,
  CheckCircle2, Clock, Loader2, ShieldCheck, ArrowLeft, Crown,
  Sparkles, ArrowRight, Share2, UserCheck, RefreshCw, Paperclip, Layout, Activity,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";
import { toast } from "sonner";
import { format, formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import BrandingSection from "@/pages/org/BrandingSection";

const ICON_MAP = {
  Sparkles, Pencil, Trash2, ArrowRight, Share2, UserPlus,
  UserCheck, RefreshCw, Paperclip, Layout, Activity,
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return format(new Date(iso), "dd MMM yyyy · HH:mm"); }
  catch { return iso; }
}

export default function OrgAdmin() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: orgData, isLoading: orgLoading, isError } = useQuery({
    queryKey: ["org-me"],
    queryFn: async () => (await api.get("/org/me")).data,
    retry: false,
  });
  const org = orgData?.org;
  const myRole = orgData?.my_role;
  const isAdmin = myRole === "admin";

  const { data: stats } = useQuery({
    queryKey: ["org-stats"],
    queryFn: async () => (await api.get("/org/stats")).data,
    enabled: !!org,
    refetchInterval: 30_000,
  });

  const { data: membersData } = useQuery({
    queryKey: ["org-members"],
    queryFn: async () => (await api.get("/org/members")).data,
    enabled: !!org,
  });

  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [activityFilter, setActivityFilter] = useState("all"); // "all" | user_id

  const { data: activityData, isLoading: activityLoading } = useQuery({
    queryKey: ["org-activity", activityFilter],
    queryFn: async () => {
      const q = activityFilter === "all" ? "" : `&actor_id=${activityFilter}`;
      return (await api.get(`/org/activity?hours=24&limit=50${q}`)).data;
    },
    enabled: !!org,
    refetchInterval: 60_000,
  });

  const renameMutation = useMutation({
    mutationFn: async (name) => (await api.patch("/org/me", { name })).data,
    onSuccess: () => {
      toast.success("Nombre de la organización actualizado");
      qc.invalidateQueries({ queryKey: ["org-me"] });
      setEditingName(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error al actualizar"),
  });

  const removeMutation = useMutation({
    mutationFn: async (userId) => (await api.delete(`/org/members/${userId}`)).data,
    onSuccess: () => {
      toast.success("Miembro eliminado");
      qc.invalidateQueries({ queryKey: ["org-members"] });
      qc.invalidateQueries({ queryKey: ["org-stats"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });

  const cancelInviteMutation = useMutation({
    mutationFn: async (inviteId) => (await api.delete(`/org/invite/${inviteId}`)).data,
    onSuccess: () => {
      toast.success("Invitación cancelada");
      qc.invalidateQueries({ queryKey: ["org-members"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });

  if (orgLoading) {
    return <div className="p-8 text-center text-muted-foreground" data-testid="org-loading">Cargando…</div>;
  }
  if (isError || !org) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center" data-testid="org-not-in-org">
        <Building2 className="h-12 w-12 mx-auto text-muted-foreground" />
        <h1 className="font-display text-2xl font-semibold mt-4">Aún no tienes una organización</h1>
        <p className="text-muted-foreground mt-2">Contrata el plan Corporativo para gestionar equipos, ver estadísticas de uso y chatear con tus miembros.</p>
        <Button className="mt-6" onClick={() => navigate("/pricing")} data-testid="org-cta-pricing">Ver planes</Button>
      </div>
    );
  }

  const members = membersData?.members || [];
  const pendingInvites = membersData?.pending_invites || [];
  const occupied = members.length + pendingInvites.length;
  const seatsPaid = org.seats_paid;
  const seatsAvailable = Math.max(0, seatsPaid - occupied);

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-6" data-testid="org-admin-page">
      <button
        onClick={() => navigate("/")}
        className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
        data-testid="org-back"
      >
        <ArrowLeft className="h-4 w-4" /> Volver al dashboard
      </button>

      {/* Header — org name + edit + summary */}
      <header className="glass-panel rounded-3xl p-6 flex flex-wrap items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <p className="text-xs uppercase tracking-wider text-blue-600 dark:text-blue-300 font-semibold flex items-center gap-2">
            <Building2 className="h-4 w-4" /> Organización · Corporativo
          </p>
          {!editingName ? (
            <div className="flex items-center gap-2 mt-1.5">
              <h1 className="font-display text-2xl md:text-3xl font-semibold" data-testid="org-name">{org.name}</h1>
              {isAdmin && (
                <button
                  onClick={() => { setEditingName(true); setNameDraft(org.name); }}
                  className="text-muted-foreground hover:text-foreground p-1"
                  data-testid="org-name-edit"
                  aria-label="Editar nombre"
                >
                  <Pencil className="h-4 w-4" />
                </button>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 mt-1.5">
              <Input
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                className="max-w-md"
                data-testid="org-name-input"
                autoFocus
              />
              <Button
                size="sm"
                onClick={() => renameMutation.mutate(nameDraft.trim())}
                disabled={renameMutation.isPending || !nameDraft.trim() || nameDraft.trim().length < 2}
                data-testid="org-name-save"
              >
                {renameMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditingName(false)} data-testid="org-name-cancel">
                Cancelar
              </Button>
            </div>
          )}
          <p className="text-sm text-muted-foreground mt-2">
            Plan {org.billing_cycle === "annual" ? "Corporativo anual" : "Corporativo mensual"} · Renueva el {fmtDate(org.current_period_end)}
          </p>
        </div>
        <div className="flex gap-3">
          <StatPill label="Asientos" value={`${occupied}/${seatsPaid}`} accent="blue" />
          <StatPill label="Disponibles" value={String(seatsAvailable)} accent="emerald" />
        </div>
      </header>

      {/* Stats grid */}
      <section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="org-stats">
        <StatCard label="Miembros" value={stats?.members_count ?? "—"} icon={Users} />
        <StatCard label="Tableros" value={stats?.boards_total ?? "—"} icon={BarChart3} />
        <StatCard label="Activos 7 días" value={stats?.active_users_last_7d ?? "—"} icon={CheckCircle2} accent="emerald" />
        <StatCard label="Logins 30 días" value={stats?.logins_last_30d ?? "—"} icon={Clock} accent="amber" />
      </section>

      {/* Branding & subdomain */}
      <BrandingSection org={org} isAdmin={isAdmin} />

      {/* Members section */}
      <section className="glass-panel rounded-3xl p-6" data-testid="org-members-section">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-display text-xl font-semibold flex items-center gap-2">
              <Users className="h-5 w-5" /> Miembros
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              El admin ve la actividad de cada miembro, pero <strong>nunca el contenido</strong> de sus tableros.
            </p>
          </div>
          {isAdmin && (
            <Button onClick={() => setInviteOpen(true)} disabled={seatsAvailable === 0} data-testid="org-add-member-btn">
              <UserPlus className="h-4 w-4 mr-1.5" />
              {seatsAvailable === 0 ? "Sin asientos disponibles" : "Agregar miembro"}
            </Button>
          )}
        </div>

        <div className="mt-5 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground border-b border-white/10">
              <tr>
                <th className="text-left font-medium py-2 px-2">Miembro</th>
                <th className="text-left font-medium py-2 px-2 hidden md:table-cell">Tableros</th>
                <th className="text-left font-medium py-2 px-2 hidden lg:table-cell">Último acceso</th>
                <th className="text-left font-medium py-2 px-2 hidden lg:table-cell">Logins 30d</th>
                <th className="text-right font-medium py-2 px-2">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {members.map((m) => (
                <tr key={m.user_id} data-testid={`org-member-${m.user_id}`}>
                  <td className="py-3 px-2">
                    <div className="flex items-center gap-2">
                      <div className="font-medium">{m.name || m.email}</div>
                      {m.org_role === "admin" && (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-blue-500/15 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded-full font-semibold">
                          <Crown className="h-3 w-3" /> Admin
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">{m.email}</div>
                  </td>
                  <td className="py-3 px-2 hidden md:table-cell">{m.boards_owned} propios · {m.boards_shared} compartidos</td>
                  <td className="py-3 px-2 hidden lg:table-cell text-muted-foreground">{fmtDate(m.last_login_at)}</td>
                  <td className="py-3 px-2 hidden lg:table-cell">{m.logins_last_30d}</td>
                  <td className="py-3 px-2 text-right">
                    {isAdmin && m.org_role !== "admin" && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-rose-500 hover:text-rose-600"
                        onClick={() => {
                          if (window.confirm(`¿Eliminar a ${m.name || m.email} del equipo?`)) removeMutation.mutate(m.user_id);
                        }}
                        data-testid={`org-remove-member-${m.user_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {pendingInvites.map((inv) => (
                <tr key={inv.invite_id} className="opacity-60" data-testid={`org-invite-${inv.invite_id}`}>
                  <td className="py-3 px-2">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-amber-500" />
                      <span className="font-medium">{inv.name || inv.email}</span>
                      <span className="text-[10px] uppercase tracking-wider bg-amber-500/15 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 rounded-full font-semibold">
                        Pendiente
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">{inv.email} · vence {fmtDate(inv.expires_at)}</div>
                  </td>
                  <td className="py-3 px-2 hidden md:table-cell text-muted-foreground italic">Invitación enviada</td>
                  <td className="py-3 px-2 hidden lg:table-cell">—</td>
                  <td className="py-3 px-2 hidden lg:table-cell">—</td>
                  <td className="py-3 px-2 text-right">
                    {isAdmin && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-rose-500 hover:text-rose-600"
                        onClick={() => {
                          if (window.confirm("¿Cancelar esta invitación?")) cancelInviteMutation.mutate(inv.invite_id);
                        }}
                        data-testid={`org-cancel-invite-${inv.invite_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {members.length === 0 && pendingInvites.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-muted-foreground">Aún no hay miembros.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Activity feed (24h) */}
      <section className="glass-panel rounded-3xl p-6" data-testid="org-activity-section">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-display text-xl font-semibold flex items-center gap-2">
              <Activity className="h-5 w-5" /> Actividad reciente
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              Últimas 24 horas · Se muestra qué hicieron los miembros, <strong>nunca el contenido</strong>.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={activityFilter} onValueChange={setActivityFilter}>
              <SelectTrigger className="w-[220px]" data-testid="org-activity-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los miembros</SelectItem>
                {members.map(m => (
                  <SelectItem key={m.user_id} value={m.user_id}>
                    {m.name || m.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-xs text-muted-foreground bg-muted/30 rounded-full px-3 py-1">
              {(activityData?.activities || []).length} eventos
            </span>
          </div>
        </div>

        {activityLoading ? (
          <div className="py-8 flex items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : !activityData?.activities?.length ? (
          <p className="py-8 text-center text-sm text-muted-foreground" data-testid="org-activity-empty">
            No hay actividad del equipo en las últimas 24 horas.
          </p>
        ) : (
          <ol className="mt-5 divide-y divide-white/5" data-testid="org-activity-feed">
            {activityData.activities.map((a) => {
              const Icon = ICON_MAP[a.icon] || Activity;
              return (
                <li
                  key={a.activity_id}
                  className="py-3 flex items-start gap-3"
                  data-testid={`org-activity-item-${a.activity_id}`}
                >
                  <div className="h-9 w-9 rounded-full bg-blue-500/15 text-blue-600 dark:text-blue-300 grid place-items-center flex-none">
                    {a.actor.picture ? (
                      <img
                        src={a.actor.picture}
                        alt={a.actor.name}
                        className="h-9 w-9 rounded-full object-cover"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <span className="text-xs font-semibold">
                        {(a.actor.name || a.actor.email || "?").slice(0, 2).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm leading-snug">
                      <span className="font-medium">{a.actor.name}</span>
                      <span className="text-muted-foreground"> {a.action_label}</span>
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1.5">
                      <Icon className="h-3 w-3" />
                      <time dateTime={a.created_at} title={fmtDate(a.created_at)}>
                        {(() => {
                          try {
                            return formatDistanceToNow(new Date(a.created_at), { addSuffix: true, locale: es });
                          } catch { return a.created_at; }
                        })()}
                      </time>
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </section>

      {/* Privacy notice */}
      <section className="glass-card rounded-2xl p-5 flex items-start gap-3 border border-emerald-500/20">
        <ShieldCheck className="h-5 w-5 text-emerald-500 flex-none mt-0.5" />
        <div>
          <p className="font-semibold text-sm">Privacidad del equipo</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Como admin puedes ver métricas de accesos y cantidad de tableros de tu equipo, pero <strong>no puedes ver el contenido</strong> de las tarjetas, comentarios o adjuntos. Los miembros mantienen la privacidad de su trabajo.
          </p>
        </div>
      </section>

      {inviteOpen && (
        <InviteMemberDialog
          seatsAvailable={seatsAvailable}
          onClose={() => setInviteOpen(false)}
          onSuccess={() => {
            qc.invalidateQueries({ queryKey: ["org-members"] });
            qc.invalidateQueries({ queryKey: ["org-stats"] });
          }}
        />
      )}
    </div>
  );
}

function StatPill({ label, value, accent = "blue" }) {
  const cls = accent === "emerald"
    ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
    : "bg-blue-500/15 text-blue-700 dark:text-blue-300";
  return (
    <div className={`rounded-xl ${cls} px-4 py-2 min-w-[100px]`}>
      <div className="text-xs uppercase tracking-wider opacity-80">{label}</div>
      <div className="font-display text-xl font-semibold">{value}</div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, accent = "blue" }) {
  const iconCls = {
    blue:    "bg-blue-500/15 text-blue-600 dark:text-blue-300",
    emerald: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300",
    amber:   "bg-amber-500/15 text-amber-600 dark:text-amber-300",
  }[accent];
  return (
    <div className="glass-card rounded-2xl p-5" data-testid={`org-stat-${label.toLowerCase().replace(/\s+/g,"-")}`}>
      <div className={`h-9 w-9 rounded-lg ${iconCls} grid place-items-center`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="font-display text-2xl font-semibold mt-3">{value}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}

function InviteMemberDialog({ seatsAvailable, onClose, onSuccess }) {
  const [mode, setMode] = useState("invite");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");

  const addMutation = useMutation({
    mutationFn: async () => {
      const body = { email: email.trim().toLowerCase(), name: name.trim() || undefined, mode };
      if (mode === "manual") body.initial_password = password;
      return (await api.post("/org/members", body)).data;
    },
    onSuccess: () => {
      toast.success(mode === "invite" ? "Invitación enviada" : "Miembro creado");
      onSuccess();
      onClose();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error al agregar miembro"),
  });

  const canSubmit = email.trim() && (mode === "invite" || password.length >= 8);

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="org-invite-dialog">
        <DialogHeader>
          <DialogTitle>Agregar miembro</DialogTitle>
          <DialogDescription>
            Quedan {seatsAvailable} asiento(s) disponible(s).
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="space-y-1.5">
            <Label>Cómo agregarlo</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="org-invite-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="invite">Enviar invitación por email</SelectItem>
                <SelectItem value="manual">Crear cuenta manualmente</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Correo</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="persona@empresa.com"
              data-testid="org-invite-email"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Nombre (opcional)</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej. María López"
              data-testid="org-invite-name"
            />
          </div>
          {mode === "manual" && (
            <div className="space-y-1.5">
              <Label>Contraseña inicial</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mínimo 8 caracteres"
                data-testid="org-invite-password"
              />
              <p className="text-[11px] text-muted-foreground">Comparte esta contraseña con la persona por un canal seguro. Debería cambiarla después de iniciar sesión.</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={() => addMutation.mutate()}
            disabled={!canSubmit || addMutation.isPending}
            data-testid="org-invite-submit"
          >
            {addMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : (mode === "invite" ? "Enviar invitación" : "Crear miembro")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
